import { z } from 'zod';
import { parseTime, parseWhenPrefix, weekdayIndex } from './notifications.js';
import { PersonRef } from './people.js';
import { RepeatRule, TodoNudge, TodoStep, repeatLabel } from './myday-repeat.js';
import { addDaysIso, zonedDate, zonedParts } from './time.js';
import { Priority } from './work-items.js';
/**
 * My day (docs/adr/0029-my-day.md): a short personal list for each person. Personal items are private
 * to their owner (admins included); synced tasks are work items assigned to the person and due today,
 * computed on read; suggestions are never added without a tap.
 */
export const TodoKind = z.enum(['personal', 'task', 'message']);
const IsoDate = z.iso.date();
/** A wall-clock time in the person's zone, 'HH:MM' (24 h). */
export const TodoTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** A work item as My day shows it. `done` follows the item's status, wherever it was changed. */
export const TodoWorkItem = z.object({
    id: z.uuid(),
    key: z.string(),
    title: z.string(),
    projectName: z.string(),
    channelId: z.uuid().nullable(),
    channelName: z.string().nullable(),
    priority: Priority,
    dueDate: z.string().nullable(),
    done: z.boolean(),
    completedAt: z.string().nullable(),
});
/** The message an item came from, while the person can still see it. */
export const TodoMessage = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    channelName: z.string(),
    author: PersonRef.nullable(),
});
export const TodoItem = z.object({
    id: z.uuid(),
    kind: TodoKind,
    title: z.string(),
    notes: z.string(),
    /** 'YYYY-MM-DD' in the owner's time zone; null only for Someday items. */
    dueOn: z.string().nullable(),
    dueTime: z.string().nullable(),
    done: z.boolean(),
    doneAt: z.string().nullable(),
    position: z.number(),
    someday: z.boolean(),
    /** Times the item moved itself to the next day unfinished. */
    movedCount: z.number().int(),
    /** Set on the pieces of an item that was broken down. */
    parentId: z.uuid().nullable(),
    workItem: TodoWorkItem.nullable(),
    message: TodoMessage.nullable(),
    /** How it repeats (docs/adr/0107); null for a one-off. Personal and from-a-message items only. */
    repeat: RepeatRule.nullable(),
    /** The date this occurrence of a repeating item is for (its `dueOn` can move; this keeps the cadence). */
    occursOn: z.string().nullable(),
    /** The item's nudge choice; null follows the person's default (`MyDay.nudgeDefault`). */
    nudge: TodoNudge.nullable(),
    /** When the next nudge goes off (ISO instant); null when none is scheduled. */
    nudgeAt: z.string().nullable(),
    /** The checklist, in order (at most MAX_TODO_STEPS). */
    steps: z.array(TodoStep),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** A task assigned to the person and due today, shown on its own (not stored in My day). */
export const SyncedTask = TodoWorkItem.extend({
    /** False when the person can see the task but its status can't be changed from here. */
    canTick: z.boolean(),
});
/**
 * Something My day offers to add with one tap. `noticed`: a request aimed at the person in chat.
 * `plan`: Chapi's proposed list for the day (only when Chapi is on).
 */
export const SuggestionKey = z.string().regex(/^(msg|plan):[0-9a-f-]{36}$/i);
export const TodoSuggestion = z.object({
    key: SuggestionKey,
    source: z.enum(['noticed', 'plan']),
    title: z.string(),
    dueOn: z.string().nullable(),
    dueTime: z.string().nullable(),
    /** One line on why it is offered: "Overdue since Tue", "Asked in #payments-app". */
    reason: z.string(),
    from: PersonRef.nullable(),
    channelId: z.uuid().nullable(),
    channelName: z.string().nullable(),
    messageId: z.uuid().nullable(),
    workItemId: z.uuid().nullable(),
    /** The words that read as the request, for "noticed" suggestions. */
    quote: z.string().nullable(),
    at: z.string().nullable(),
});
export const MyDay = z.object({
    today: z.string(),
    timeZone: z.string(),
    greetingName: z.string(),
    /** Today's items that did not move here from an earlier day: open, or crossed off today. */
    items: z.array(TodoItem),
    /** Items that moved here from an earlier day unfinished ("overdue, moved"). */
    moved: z.array(TodoItem),
    /** Tasks assigned to the person and due today. */
    synced: z.array(SyncedTask),
    /** Dated after today ("coming up"). */
    upcoming: z.array(TodoItem),
    someday: z.array(TodoItem),
    suggestions: z.object({ noticed: z.array(TodoSuggestion), plan: z.array(TodoSuggestion) }),
    /** Items that moved 3 times or more, offered once a day; null when there is nothing, or done today. */
    tidyUp: z.array(TodoItem).nullable(),
    stats: z.object({
        /** Crossed off today (items and synced tasks). */
        done: z.number().int(),
        /** Everything on today's list, done or not. */
        total: z.number().int(),
        open: z.number().int(),
        /** Consecutive days, up to today, with at least one thing crossed off. */
        streak: z.number().int(),
    }),
    /** Chapi is on for the workspace and the person (Plan my day, "Chapi noticed" label). */
    chapiOn: z.boolean(),
    /** Tasks are on and the person may create and tick them. */
    tasksOn: z.boolean(),
    /** The person's default nudge for items with a time (setting `reminders.myDayNudge`, docs/adr/0107). */
    nudgeDefault: TodoNudge,
});
export const CreateTodoInput = z.object({
    /** With `parse`, the raw quick-add text: dates ("tomorrow 3pm") and `#channel` are read from it. */
    title: z.string().trim().min(1).max(300),
    parse: z.boolean().default(false),
    notes: z.string().max(4000).optional(),
    dueOn: IsoDate.nullable().optional(),
    dueTime: TodoTime.nullable().optional(),
    someday: z.boolean().optional(),
    /** "Add to my day" on a message. */
    messageId: z.uuid().optional(),
    /** Pin a task to today (it is then on the list until it is done). */
    workItemId: z.uuid().optional(),
    /** Repeat it (docs/adr/0107); quick add also reads "every monday" from the text. Not for tasks. */
    repeat: RepeatRule.optional(),
    nudge: TodoNudge.optional(),
}).refine((v) => !(v.messageId && v.workItemId), { message: 'An item comes from a message or a task, not both' });
export const CreateTodoResponse = z.object({
    /** The new item; null when `#channel` made a task instead (it shows as a synced task when due today). */
    item: TodoItem.nullable(),
    /** The task created from `#channel`. */
    workItem: z.object({ id: z.uuid(), key: z.string(), title: z.string(), dueDate: z.string().nullable() }).nullable(),
});
export const UpdateTodoInput = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    notes: z.string().max(4000).optional(),
    dueOn: IsoDate.nullable().optional(),
    dueTime: TodoTime.nullable().optional(),
    done: z.boolean().optional(),
    position: z.number().finite().optional(),
    someday: z.boolean().optional(),
    /** Set a repeat, or `null` to stop repeating (docs/adr/0107). */
    repeat: RepeatRule.nullable().optional(),
    /** The nudge for an item with a time; `null` goes back to the person's default. */
    nudge: TodoNudge.nullable().optional(),
});
/** The 30-second tidy-up's choices for an item that keeps moving. */
export const TidyAction = z.discriminatedUnion('action', [
    z.object({ action: z.literal('today') }),
    z.object({ action: z.literal('day'), dueOn: IsoDate }),
    z.object({ action: z.literal('task'), projectId: z.uuid(), assigneeUserId: z.uuid(), dueOn: IsoDate.nullable().optional() }),
    z.object({ action: z.literal('break'), parts: z.array(z.string().trim().min(1).max(300)).min(1).max(10) }),
    z.object({ action: z.literal('let_go') }),
]);
export const TidyResult = z.object({
    item: TodoItem.nullable(),
    parts: z.array(TodoItem),
    workItem: z.object({ id: z.uuid(), key: z.string() }).nullable(),
});
export const SetTaskDoneInput = z.object({ done: z.boolean() });
export const SetTaskDoneResponse = z.object({ task: SyncedTask });
export const AcceptSuggestionsInput = z.object({ keys: z.array(SuggestionKey).min(1).max(20) });
export const AcceptSuggestionsResponse = z.object({ items: z.array(TodoItem) });
export const DismissSuggestionInput = z.object({ key: SuggestionKey });
// --- Desktop sticky notes (docs/adr/0105) ---------------------------------------------------------
/** The five note colours (light and dark values in packages/ui-tokens `noteColours`). Yellow is the default. */
export const NoteColour = z.enum(['yellow', 'pink', 'blue', 'green', 'violet']);
/** At most this many pinned notes per person per workspace. */
export const MAX_DESKTOP_NOTES = 20;
export const NOTE_LIMIT_MESSAGE = `You can pin up to ${MAX_DESKTOP_NOTES} notes. Unpin one to pin another.`;
/**
 * A My day item pinned to the desktop, with what the note shows. `itemId` for an item on the list (personal, from a message,
 * or a linked task), `workItemId` for a task (a synced task has only that). Private to its owner.
 */
export const DesktopNote = z.object({
    id: z.uuid(),
    colour: NoteColour,
    kind: TodoKind,
    itemId: z.uuid().nullable(),
    workItemId: z.uuid().nullable(),
    title: z.string(),
    notes: z.string(),
    dueOn: z.string().nullable(),
    dueTime: z.string().nullable(),
    done: z.boolean(),
    /** The task's key ("SIM-418") for a task note. */
    workItemKey: z.string().nullable(),
    /** The message an item came from, for Open in Chapsi. */
    channelId: z.uuid().nullable(),
    messageId: z.uuid().nullable(),
    /** A repeating item's rule in words ("Every Friday"); its note stays pinned and shows the next occurrence (docs/adr/0107). */
    repeat: z.string().nullable(),
    /** The item's checklist, tickable on the note. */
    steps: z.array(TodoStep),
    pinnedAt: z.string(),
});
export const DesktopNotes = z.object({ notes: z.array(DesktopNote), limit: z.number().int() });
export const PinNoteInput = z
    .object({ itemId: z.uuid().optional(), workItemId: z.uuid().optional(), colour: NoteColour.optional() })
    .refine((v) => !!v.itemId !== !!v.workItemId, { message: 'Pin an item or a task' });
export const UpdateNoteInput = z.object({ colour: NoteColour });
/** A pin as the person's own export lists it: which item, its colour, when. */
export const ExportedNote = z.object({ id: z.uuid(), itemId: z.uuid().nullable(), workItemId: z.uuid().nullable(), colour: NoteColour, pinnedAt: z.string() });
export const MyDayExport = z.object({ exportedAt: z.string(), items: z.array(TodoItem), notes: z.array(ExportedNote) });
/** What Plan my day proposes; carried on Chapi's morning summary (docs/adr/0029-my-day.md). */
export const TodoPlanItem = z.object({ key: SuggestionKey, title: z.string(), reason: z.string() });
const CONNECTORS = new Set(['by', 'before', 'due', 'until']);
const CHANNEL = /^#([a-z0-9][a-z0-9_-]{0,79})[.,;:!?]*$/i;
const pad = (n) => String(n).padStart(2, '0');
/** "before 3" in a request means 3 pm, not 3 am: a bare hour up to 7 is read as the afternoon. */
function bareTime(word) {
    const t = parseTime(word?.replace(/[.,;:!?]+$/, ''), true);
    if (!t)
        return null;
    if (!/(am|pm)$/i.test(word ?? '') && t.h >= 1 && t.h <= 7)
        return { h: t.h + 12, m: t.m };
    return t;
}
/**
 * Reads quick-add text: "call Sam tomorrow 3pm" → title "call Sam", tomorrow, 15:00. The date phrase
 * can be anywhere; it is read with the same reader as `/remind` (parseWhenPrefix). A `#channel` token
 * asks for a task in that channel. Times without a day mean today (even if the time has passed: it's a
 * list, not an alarm); days without a time have no time.
 */
export function parseQuickAdd(text, now, timeZone) {
    const all = text.trim().replace(/^\/todo\b\s*/i, '').split(/\s+/).filter(Boolean);
    let channel = null;
    const words = [];
    for (const w of all) {
        const m = CHANNEL.exec(w);
        if (m && !channel)
            channel = m[1].toLowerCase();
        else
            words.push(w);
    }
    const today = zonedDate(now, timeZone);
    let dueOn = null;
    let time = null;
    let cut = null;
    let repeat;
    for (let i = 0; i < words.length && !cut; i++) {
        const lead = words[i].toLowerCase();
        const connector = CONNECTORS.has(lead);
        const from = connector ? i + 1 : i;
        // Trailing punctuation would stop the reader ("Friday?"); compare without it.
        const rest = words.slice(from).map((w) => w.replace(/[.,;:!?]+$/, ''));
        if (!rest.length)
            continue;
        // "before 3", "by 5": a bare hour after a connector.
        if (connector && !weekdayLike(rest[0])) {
            const t = /^\d{1,2}([:.]\d{2})?(am|pm)?$/i.test(rest[0]) ? bareTime(rest[0]) : null;
            if (t) {
                time = t;
                cut = { start: i, end: from + 1 };
                break;
            }
        }
        const when = parseWhenPrefix(rest, now, timeZone, { repeat: !connector });
        if (!when || 'error' in when)
            continue;
        cut = { start: i, end: from + when.consumed };
        if (when.repeat)
            repeat = when.repeat;
        if (when.offsetMinutes !== null) {
            const at = new Date(now.getTime() + when.offsetMinutes * 60_000);
            const p = zonedParts(at, timeZone);
            dueOn = zonedDate(at, timeZone);
            time = { h: p.hour, m: p.minute };
        }
        else {
            dueOn = when.date;
            time = when.time;
        }
    }
    if (time && !dueOn)
        dueOn = today;
    const left = cut ? [...words.slice(0, cut.start), ...words.slice(cut.end)] : words;
    // "call Sam at" / "send it on" left dangling by the cut.
    while (left.length && ['at', 'on', 'by', 'before', 'due', 'for'].includes(left[left.length - 1].toLowerCase()))
        left.pop();
    const title = left.join(' ').trim();
    return {
        title: title || words.join(' ').trim() || text.trim(),
        dueOn,
        dueTime: time ? `${pad(time.h)}:${pad(time.m)}` : null,
        channel,
        ...(repeat ? { repeat } : {}),
    };
}
function weekdayLike(word) {
    return weekdayIndex(word) >= 0 || ['today', 'tomorrow', 'tmrw', 'next'].includes(word.toLowerCase());
}
/** "3:00 pm" for '15:00'. */
export function formatTodoTime(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    const hour = h % 12 || 12;
    return `${hour}:${pad(m)} ${h < 12 ? 'am' : 'pm'}`;
}
/** "Today", "Tomorrow", "Fri", "Mon 26 Oct", relative to `today` ('YYYY-MM-DD'). */
export function formatTodoDay(date, today) {
    if (date === today)
        return 'Today';
    if (date === addDaysIso(today, 1))
        return 'Tomorrow';
    if (date === addDaysIso(today, -1))
        return 'Yesterday';
    const d = new Date(`${date}T00:00:00Z`);
    const days = Math.round((d.getTime() - new Date(`${today}T00:00:00Z`).getTime()) / 86_400_000);
    const weekday = d.toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'UTC' });
    if (days > 0 && days < 7)
        return weekday;
    return `${weekday} ${d.getUTCDate()} ${d.toLocaleDateString('en-AU', { month: 'short', timeZone: 'UTC' })}`;
}
/** The chips under quick add: how Chapsi reads what was typed. */
export function quickAddChips(q, today) {
    const chips = [];
    // A task from #channel keeps its own due date: it never repeats.
    if (q.repeat && !q.channel)
        chips.push(repeatLabel(q.repeat));
    if (q.dueOn && (q.dueOn !== today || !q.dueTime))
        chips.push(formatTodoDay(q.dueOn, today));
    if (q.dueTime)
        chips.push(q.dueOn && q.dueOn !== today ? formatTodoTime(q.dueTime) : `Today ${formatTodoTime(q.dueTime)}`);
    chips.push(q.channel ? `Task in #${q.channel}` : 'Just me');
    return chips;
}
// --- "Chapi noticed": requests aimed at someone in chat -------------------------------------------
const REQUEST = /\b(can|could|would|will)\s+(you|u)\b|\bplease\b|\bpls\b|\bplz\b|\bneed\s+you\s+to\b|\bany\s+chance\b|\bwhen\s+you\s+(get|have)\s+a\s+(sec|second|chance|minute|moment)\b|\bmind\s+\w+ing\b|\bcan\s+someone\b|\bcould\s+someone\b/i;
const MENTION = /<@([0-9a-f-]{36})>/gi;
/**
 * Whether a message reads like a request aimed at `userId`: it @mentions them and asks for something
 * ("can you…", "please…", "need you to…"). Deliberately cheap and deterministic (no model call): it can
 * miss some and offer a few that aren't, and the person dismisses those once.
 */
export function isRequestFor(body, userId) {
    const mentioned = [...body.matchAll(MENTION)].some((m) => m[1].toLowerCase() === userId.toLowerCase());
    return mentioned && REQUEST.test(body.replace(MENTION, ' '));
}
const LEAD = /^(hey|hi|hello|yo|morning|g'?day|ok|okay|so|and|also|quick one|quick q)\b[\s,!:.-]*/i;
const ASK = /^(can|could|would|will)\s+(you|u)\s+(please\s+|pls\s+)?|^(please|pls|plz)\s+|^need\s+you\s+to\s+|^any\s+chance\s+(you\s+)?(could|can)\s+|^when\s+you\s+(get|have)\s+a\s+(sec|second|chance|minute|moment)[\s,]*(can|could)?\s*(you\s+)?/i;
/**
 * A to-do title from a request: "@Nathan can you check the BPoint refund log before 3?" →
 * "Check the BPoint refund log". The date phrase is left to `parseQuickAdd`.
 */
export function requestTitle(body, now, timeZone) {
    const plain = body.replace(MENTION, ' ').replace(/\s+/g, ' ').trim();
    // The sentence that asks: the first one with a request in it.
    const sentences = plain.split(/(?<=[.?!])\s+/);
    const quote = (sentences.find((s) => REQUEST.test(s)) ?? sentences[0] ?? plain).trim();
    let t = quote;
    for (let i = 0; i < 3; i++)
        t = t.replace(LEAD, '').replace(/^[,\s]+/, '');
    // "Field list is on the task, can you confirm the scope?": the ask starts where "can you" does.
    const ask = /\b(?:(?:can|could|would|will)\s+(?:you|u)|need\s+you\s+to|any\s+chance\s+(?:you\s+)?(?:could|can))\s+(?:please\s+|pls\s+)?/i.exec(t);
    if (ask && t.slice(ask.index + ask[0].length).trim())
        t = t.slice(ask.index + ask[0].length);
    else
        t = t.replace(ASK, '');
    t = t.replace(/\b(please|pls|plz)\b/gi, '').replace(/[?!.]+$/, '').replace(/\s+(thanks|thx|ta|cheers)$/i, '');
    const q = parseQuickAdd(t, now, timeZone);
    let title = q.title.replace(/\s+/g, ' ').replace(/[\s,]+$/, '').trim();
    if (title.length > 120)
        title = `${title.slice(0, 119).trimEnd()}…`;
    title = title ? title[0].toUpperCase() + title.slice(1) : quote.slice(0, 120);
    return { title, dueOn: q.dueOn, dueTime: q.dueTime, quote: quote.length > 240 ? `${quote.slice(0, 239)}…` : quote };
}
// --- Streak -------------------------------------------------------------------------------------
/**
 * Consecutive days, ending today or yesterday, with at least one thing crossed off. `days` are
 * 'YYYY-MM-DD' dates in the person's time zone, in any order. Today counts once it happens; until
 * then yesterday's run is still alive.
 */
export function streakFrom(days, today) {
    const set = new Set(days);
    let day = set.has(today) ? today : addDaysIso(today, -1);
    let n = 0;
    while (set.has(day)) {
        n++;
        day = addDaysIso(day, -1);
    }
    return n;
}
/** Items moved this many times are offered in the day's tidy-up. */
export const TIDY_AFTER_MOVES = 3;
/**
 * Plan my day, by rules (no model call): the person's open tasks that are overdue, oldest first, then
 * the rest of the next six days, up to five. Tasks due today are already on the list (synced).
 */
export function planFromTasks(tasks, today, max = 5) {
    const weekEnd = addDaysIso(today, 6);
    const overdue = tasks.filter((t) => t.dueDate && t.dueDate < today).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    const soon = tasks.filter((t) => t.dueDate && t.dueDate > today && t.dueDate <= weekEnd).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    return [...overdue, ...soon].slice(0, max).map((t) => ({
        key: `plan:${t.id}`,
        title: t.title,
        reason: t.dueDate < today ? `Overdue since ${formatTodoDay(t.dueDate, today).replace('Yesterday', 'yesterday')}` : `Due ${formatTodoDay(t.dueDate, today).replace('Tomorrow', 'tomorrow')}`,
    }));
}
//# sourceMappingURL=myday.js.map